import { useState, useEffect, useMemo } from 'react';
import { Typography, useElevation } from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import classnames from 'classnames';
import PageSlider from 'Molecules/PageSlider';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';

type PaginationProps = {
  total: number;
  onChange: (params: object) => void;
  className?: string;
};

type PageSliderOption = { value: number; label: string };

const ResultsRange: React.FC<{
  limit: number;
  total: number;
  offset: number;
}> = ({ limit, total, offset }) => {
  const rangeStart = offset + 1;
  const rangeEnd = limit < total ? limit : total;
  return (
    <div className='h-full flex items-center'>
      <Typography
        variant='body'
        data-cy={`total_results-${total}`}
        className='text-neutral-black'
      >
        {intl.get('PAGINATION.RESULTS', {
          range:
            rangeEnd > 0 && rangeEnd > rangeStart
              ? `${rangeStart}-${rangeEnd}`
              : rangeEnd,
          total: total,
        })}
      </Typography>
    </div>
  );
};

const ClientPagination = ({ total, onChange, className }: PaginationProps) => {
  const skim = useElevation(1);
  const [currentPage, setCurrentPage] = useState<PageSliderOption>({
    value: 1,
    label: '1',
  });

  useEffect(() => {
    onChange({
      limit: DEFAULT_PAGE_SIZE * currentPage.value,
      offset: DEFAULT_PAGE_SIZE * (currentPage.value - 1),
    });
  }, [currentPage, onChange]);

  const pageCount = useMemo<number>(() => {
    const totalPages = Math.ceil(total / DEFAULT_PAGE_SIZE);
    if (currentPage.value > totalPages && totalPages > 0) {
      setCurrentPage({ value: totalPages, label: totalPages.toString() });
    }
    return totalPages;
  }, [currentPage, total]);

  return (
    // Renders absolutely over the bottom of the page: consumers must cap their
    // table's scroll region (e.g. max-h-request-details) so its horizontal
    // scrollbar is not covered by this bar (CGP-210).
    <div
      data-cy='pagination-container'
      className={classnames(
        'absolute bottom-0 left-0 py-3 z-10',
        'px-6 bg-neutral-white w-full',
        'flex items-center justify-between',
        skim,
        className
      )}
    >
      <ResultsRange
        total={total}
        limit={DEFAULT_PAGE_SIZE * currentPage.value}
        offset={DEFAULT_PAGE_SIZE * (currentPage.value - 1)}
      />
      <div className='flex h-full items-center'>
        <PageSlider
          total={total}
          pages={pageCount}
          offset={currentPage.value}
          limit={DEFAULT_PAGE_SIZE}
          setOffset={(value: number) =>
            setCurrentPage({ value, label: value.toString() })
          }
        />
      </div>
    </div>
  );
};

export default ClientPagination;
